<template>
  <Autocomplete
    ref="autocomplete"
    v-bind="$attrs"
    v-on="$listeners"
    v-model="innerValue"
    :items="itemsForMenu"
    :hide-selected="hasTypedSearch ? false : hideSelected"
    :search-input.sync="search"
    :loading="loading || !!$attrs.loading"
    :menu-props="mergedMenuProps"
    :item-text="itemText"
    :item-value="itemValue"
    :placeholder="placeholder"
    :hide-no-data="hideNoData"
    :clearable="clearable"
    :return-object="returnObject"
    multiple
    chips
    small-chips
    :outlined="outlined"
    :dense="dense"
    :filter="() => true"
    :disabled="isComponentDisabled"
    :class="[
      'paginated-autocomplete-multi',
      $attrs.class,
      `${groupByHeader ? 'paginated-autocomplete-multi__group': ''}`,
      { 'paginated-autocomplete-multi--searching': hasTypedSearch },
    ]"
    :style="[$attrs.style]"
    @focus="handleFocus"
    @blur="handleBlur"
    @click="handleClick"
    @update:search-input="handleSearch"
    @click:clear="clearSearch"
  >
    <template v-if="!groupByHeader" v-slot:item="{ item }">
      <div
        class="paginated-autocomplete-multi__item d-flex flex-row"
        @click.stop="toggleItem(item)"
        @mousedown.stop.prevent
        @mouseup.stop
      >
        <v-simple-checkbox
          class="paginated-autocomplete-multi__checkbox"
          :value="isSelected(item)"
          :ripple="false"
          @click.stop
          @input="(checked) => onCheckboxChange(item, checked)"
        />

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <div
              class="paginated-autocomplete-multi__content"
              v-bind="attrs"
              v-on="on"
            >
              <div class="paginated-autocomplete-multi__title">
                <slot name="item-title" :item="item">
                  {{ getText(item) }}
                </slot>
              </div>

              <div
                v-if="subtitleKey && item && item[subtitleKey]"
                class="paginated-autocomplete-multi__subtitle"
              >
                <slot name="item-subtitle" :item="item">
                  {{ item[subtitleKey] }}
                </slot>
              </div>
            </div>
          </template>

          <span>{{ getText(item) }}</span>
        </v-tooltip>
      </div>
    </template>

    <template v-else v-slot:item="{ item }">
      <template class="px-0">
        <v-list-item @mousedown.stop.prevent>
          <template
            v-slot:default="{ active }">
            <v-list-item-action>
              <v-checkbox
                :input-value="innerValue.includes(item.name)"
                @change="onCheckboxChange(item.name, !innerValue.includes(item.name))"
              />
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title
                v-text="item.name"
              />
            </v-list-item-content>
          </template>
        </v-list-item>
      </template>
    </template>

    <template v-slot:selection="{ item, index }">
      <template v-if="index === 0 && selectedValues.length > 0">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              v-bind="attrs"
              v-on="on"
              small
              close
              color="primary"
              class="paginated-autocomplete-multi__chip paginated-autocomplete-multi__chip--main"
              :style="mainChipStyle"
              @click:close="removeValue(item)"
            >
              <span class="paginated-autocomplete-multi__chip-text">
                {{ formatValue(item) }}
              </span>
            </v-chip>
          </template>
          <span>{{ formatValue(item) }}</span>
        </v-tooltip>
      </template>

      <template v-else-if="index === 1 && selectedValues.length > 1">
        <v-menu
          open-on-hover
          offset-y
          :close-on-content-click="false"
          transition="scale-transition"
          max-width="360"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              v-bind="attrs"
              v-on="on"
              small
              close
              color="primary"
              class="paginated-autocomplete-multi__chip paginated-autocomplete-multi__chip--group"
              @click:close="clearGrouped"
            >
              +{{ selectedValues.length - 1 }}
            </v-chip>
          </template>

          <v-card class="paginated-autocomplete-multi__group-card">
            <v-list
              dense
              class="py-0"
              style="max-height: 240px; overflow-y: auto"
            >
              <v-list-item
                v-for="value in groupedValues"
                :key="keyForValue(value)"
                class="px-2"
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-list-item-content class="py-1" v-bind="attrs" v-on="on">
                      <v-list-item-title class="text-body-2">
                        {{ formatValue(value) }}
                      </v-list-item-title>
                    </v-list-item-content>

                    <v-list-item-action class="ma-0">
                      <v-btn
                        icon
                        small
                        @click.stop.prevent="removeValue(value)"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </template>
                  <span>{{ formatValue(value) }}</span>
                </v-tooltip>
              </v-list-item>
            </v-list>

            <v-divider />

            <v-card-actions class="py-1 px-2">
              <v-spacer />
              <v-btn text small @click.stop.prevent="clearGrouped">
                Limpar tudo
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </template>
    </template>

    <template v-if="infinite" v-slot:append-item>
      <v-divider />
      <v-list-item>
        <v-list-item-content class="text-center">
          <div v-if="hasMoreItems">
            <v-progress-circular
              v-if="loadingMore"
              size="20"
              indeterminate
              color="primary"
            />
            <div
              v-intersect.quiet="onIntersect"
              style="height: 1px; width: 100%"
            />
          </div>

          <small v-else-if="items.length > 0" class="text--disabled">
            {{ endText }}
          </small>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-slot:prepend-item v-if="showSearchInfo">
      <v-list-item key="search-info">
        <v-list-item-content>
          <v-list-item-title class="caption text--secondary">
            Buscando por: "{{ search }}"
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-divider />
    </template>

    <template v-slot:no-data>
      <v-list-item v-if="showNoData">
        <v-list-item-content class="text-center">
          <small class="text--secondary">{{ noDataText }}</small>
        </v-list-item-content>
      </v-list-item>
    </template>
  </Autocomplete>
</template>

<script>
import { debounce } from "lodash";
import Autocomplete from "@/components/Default/Autocomplete.vue";

export default {
  name: "PaginatedAutocompleteMulti",
  inheritAttrs: false,

  components: {
    Autocomplete,
  },

  props: {
    value: { default: () => [] },

    enabled: { type: Boolean, default: true },

    fetch: { type: Function, required: true },

    groupByHeader: { type: Boolean, default: true },

    itemText: { type: [String, Function], default: "name" },
    itemValue: { type: String, default: "id" },
    subtitleKey: { type: String, default: "" },

    placeholder: { type: String, default: "Selecione" },
    noDataText: { type: String, default: "Nenhum resultado encontrado" },
    endText: { type: String, default: "Todos os itens foram carregados" },

    initialSearch: { type: String, default: "" },
    minSearchChars: { type: Number, default: 1 },
    debounceMs: { type: Number, default: 500 },
    loadOnFocus: { type: Boolean, default: true },

    infinite: { type: Boolean, default: true },
    pageSize: { type: Number, default: 20 },

    clearable: { type: Boolean, default: true },
    hideNoData: { type: Boolean, default: false },
    hideSelected: { type: Boolean, default: false },
    returnObject: { type: Boolean, default: false },
    refreshListItems: { type: Boolean, default: false },

    outlined: { type: Boolean, default: false },
    dense: { type: Boolean, default: false },

    menuProps: {
      type: Object,
      default: () => ({
        maxHeight: 304,
        "offset-y": true,
        "nudge-bottom": 10,
      }),
    },

    prependItems: {
      type: Function,
      default: null,
    },
    mapItem: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      innerValue: [],
      items: [],
      search: this.initialSearch,

      loading: false,
      loadingMore: false,

      currentPage: 0,
      hasMoreItems: true,
      currentSearch: this.initialSearch,

      requestSequence: 0,
      initialDataLoaded: false,

      selectedById: {},

      bootstrapped: false,

      menuWidth: null,
      hasFocus: false,
    };
  },

  computed: {
    mergedMenuProps() {
      const w = this.menuWidth || undefined;

      const base = {
        maxHeight: 304,
        "offset-y": true,
        "nudge-bottom": 10,
        "close-on-content-click": false,
        ...(this.menuProps || {}),
      };

      const existingClass = base["content-class"] || base.contentClass || "";
      base["content-class"] = [
        "paginated-autocomplete-multi__menu",
        existingClass,
      ]
        .filter(Boolean)
        .join(" ");

      if (w) {
        base.minWidth = w;
        base.maxWidth = w;
        base["min-width"] = w;
        base["max-width"] = w;
      }

      return base;
    },

    isComponentDisabled() {
      const attrDisabled =
        this.$attrs.disabled === "" ||
        this.$attrs.disabled === true ||
        this.$attrs.disabled === "disabled";
      return !this.enabled || !!attrDisabled;
    },

    selectedValues() {
      return Array.isArray(this.innerValue) ? this.innerValue : [];
    },

    groupedValues() {
      return this.selectedValues.slice(1);
    },

    mainChipStyle() {
      if (this.selectedValues.length > 1)
        return { maxWidth: "calc(100% - 85px)" };
      return { maxWidth: "100%" };
    },

    hasTypedSearch() {
      return (this.search || "").length > 0;
    },

    itemsForMenu() {
      const current = Array.isArray(this.items) ? this.items : [];
      if (this.groupByHeader) return current;

      const byId = new Map();
      current.forEach((i) => {
        const id = this.getIdFromItem(i);
        if (id != null) byId.set(id, i);
      });

      const selectedSet = new Set();
      const selectedTop = [];

      for (const v of this.selectedValues) {
        const id = this.getIdFromValue(v);
        if (id == null || selectedSet.has(id)) continue;
        selectedSet.add(id);

        const obj =
          (typeof v === "object" ? v : null) ||
          byId.get(id) ||
          this.selectedById[id] ||
          { [this.itemValue]: id, __label: String(v) };

        selectedTop.push(obj);
      }

      if (this.hasTypedSearch) {
        const rest = current.filter((i) => {
          const id = this.getIdFromItem(i);
          return id == null || !selectedSet.has(id);
        });
        return [...selectedTop, ...rest];
      }

      const out = current.slice();
      const existing = new Set();
      out.forEach((i) => {
        const id = this.getIdFromItem(i);
        if (id != null) existing.add(id);
      });

      selectedTop.forEach((obj) => {
        const id = this.getIdFromItem(obj);
        if (id != null && !existing.has(id)) {
          out.push(obj);
          existing.add(id);
        }
      });

      return out;
    },

    showSearchInfo() {
      return (
        this.search &&
        this.search.length >= this.minSearchChars
      );
    },

    showNoData() {
      return this.search && this.items.length === 0 && !this.loading;
    },
  },

  watch: {
    refreshListItems: {
      handler(v) {
        if (v) {
          this.refreshItems();
          this.$emit('listUpdated');
        }
      },
    },

    enabled: {
      immediate: true,
      handler(v) {
        if (!v) {
          this.deactivate();
          return;
        }
        this.activate();
      },
    },

    value: {
      immediate: true,
      handler(v) {
        if (!this.enabled) return;

        const normalized = this.normalizeValue(v);

        if (this.areValuesEqual(normalized, this.innerValue)) return;

        this.innerValue = normalized.slice();
        this.cacheSelected(this.innerValue);
      },
    },

    innerValue: {
      deep: true,
      handler(newV, oldV) {
        if (!this.enabled) return;
        if (newV === oldV) return;

        const normalized = this.normalizeValue(newV);
        this.cacheSelected(normalized);

        if (this.areValuesEqual(normalized, this.value)) return;

        this.$emit("input", normalized.slice());
      },
    },

    initialSearch(newVal) {
      this.search = newVal;
      this.currentSearch = newVal;

      if (!this.enabled) return;

      if (newVal) {
        this.load(false);
      } else {
        this.loadInitialData(true);
      }
    },
  },

  created() {
    this.debouncedSearch = debounce(
      this.handleSearchDebounced,
      this.debounceMs
    );
  },

  mounted() {
    this.updateMenuWidth();
    this.observeInputResize();
    window.addEventListener("resize", this.updateMenuWidth, { passive: true });
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updateMenuWidth);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.debouncedSearch.cancel();
  },

  methods: {
    updateMenuWidth() {
      this.$nextTick(() => {
        const wrapper = this.$refs.autocomplete;
        const width = typeof wrapper?.getInputWidth === "function"
          ? wrapper.getInputWidth()
          : 0;
        if (width > 0) {
          this.menuWidth = width;
        }
      });
    },

    observeInputResize() {
      if (typeof ResizeObserver === "undefined") return;

      this.$nextTick(() => {
        const wrapper = this.$refs.autocomplete;
        const el = typeof wrapper?.getInputElement === "function"
          ? wrapper.getInputElement()
          : null;
        if (!el) return;

        this.resizeObserver = new ResizeObserver(() => {
          this.updateMenuWidth();
        });
        this.resizeObserver.observe(el);
      });
    },

    focus() {
      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.focus();
      }
    },

    openMenu() {
      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.openMenu();
      }
    },

    closeMenu() {
      const autocomplete = this.$refs.autocomplete;
      const inner = autocomplete && autocomplete.$refs.autocomplete;
      if (inner) {
        inner.isMenuActive = false;
      }
    },

    commitSearchValue() {
      return null;
    },

    bootstrapLoad() {
      if (!this.enabled) return;
      if (this.bootstrapped) return;

      this.bootstrapped = true;

      if (
        this.currentSearch &&
        this.currentSearch.length >= this.minSearchChars
      ) {
        this.load(false);
        return;
      }

      this.loadInitialData(false);
    },

    deactivate() {
      this.requestSequence++;
      this.loading = false;
      this.loadingMore = false;

      this.items = [];
      this.search = "";
      this.currentSearch = "";

      this.currentPage = 0;
      this.hasMoreItems = true;
      this.initialDataLoaded = false;

      this.selectedById = {};
      this.innerValue = [];
      this.bootstrapped = false;
      this.$emit("input", []);
    },

    activate() {
      const normalized = this.normalizeValue(this.value);
      this.innerValue = normalized.slice();
      this.cacheSelected(this.innerValue);
      this.bootstrapLoad();
    },

    normalizeValue(v) {
      if (Array.isArray(v)) return v;
      if (v == null) return [];
      return [v];
    },

    areValuesEqual(a, b) {
      const va = this.normalizeValue(a);
      const vb = this.normalizeValue(b);

      if (va.length !== vb.length) return false;

      for (let i = 0; i < va.length; i++) {
        if (this.getIdFromValue(va[i]) !== this.getIdFromValue(vb[i]))
          return false;
      }
      return true;
    },

    getIdFromItem(item) {
      if (item == null) return null;
      if (typeof item === "object") return item[this.itemValue];
      return item;
    },

    getIdFromValue(v) {
      if (v == null) return null;
      if (typeof v === "object") return v[this.itemValue];
      return v;
    },

    getText(item) {
      if (!item) return "";
      if (item.__label != null) return item.__label;
      return typeof this.itemText === "function"
        ? this.itemText(item)
        : item[this.itemText];
    },

    cacheSelected(values) {
      const arr = Array.isArray(values) ? values : [];

      if (this.returnObject) {
        arr.forEach((v) => {
          const id = this.getIdFromValue(v);
          if (id != null && typeof v === "object")
            this.$set(this.selectedById, id, v);
        });
        return;
      }

      arr.forEach((v) => {
        const id = this.getIdFromValue(v);
        if (id == null) return;

        const fromItems = this.items.find((i) => this.getIdFromItem(i) === id);
        const existing = this.selectedById[id];

        const resolved =
          fromItems || existing || { [this.itemValue]: id, __label: String(v) };

        this.$set(this.selectedById, id, resolved);
      });
    },

    formatValue(v) {
      if (v == null) return "";

      const id = this.getIdFromValue(v);

      if (typeof v === "object") return this.getText(v);

      const cached = this.selectedById[id];
      if (cached) return this.getText(cached);

      const found = this.items.find((i) => this.getIdFromItem(i) === id);
      if (found) return this.getText(found);

      return String(v);
    },

    keyForValue(v) {
      const id = this.getIdFromValue(v);
      return id != null ? String(id) : String(v);
    },

    isSelected(item) {
      const id = this.getIdFromItem(item);
      if (id == null) return false;
      return this.selectedValues.some((v) => this.getIdFromValue(v) === id);
    },

    getStatusList(item) {
      if (!item || !Array.isArray(item.status)) return [];
      return item.status;
    },

    isGroupStatusSelected(status) {
      return this.selectedValues.includes(status);
    },

    toggleGroupStatus(status) {
      if (!this.enabled) return;
      this.onGroupStatusChange(status, !this.isGroupStatusSelected(status));
    },

    onGroupStatusChange(status, checked) {
      if (!this.enabled) return;

      if (checked) {
        if (this.selectedValues.includes(status)) return;
        this.innerValue = [...this.selectedValues, status];
      } else {
        this.innerValue = this.selectedValues.filter((v) => v !== status);
      }

      this.$emit("groupChange", status);
    },

    toggleItem(item) {
      if (!this.enabled) return;
      this.onCheckboxChange(item, !this.isSelected(item));
    },

    onCheckboxChange(item, checked) {
      if (!this.enabled) return;

      const id = this.getIdFromItem(item);
      if (id == null) return;

      if (checked) {
        if (!this.isSelected(item)) {
          if (this.returnObject) {
            this.innerValue = [...this.selectedValues, item];
            this.$set(this.selectedById, id, item);
          } else {
            this.innerValue = [...this.selectedValues, id];
            this.$set(this.selectedById, id, item);
          }
        }
      } else {
        this.removeById(id);
      }

      this.debouncedSearch.cancel();
      this.currentSearch = '';
      this.search = '';
      this.requestSequence += 1;
      this.loading = false;
      this.loadingMore = false;
      this.loadInitialData(true);
      this.$nextTick(() => {
        const autocomplete = this.$refs.autocomplete;
        if (autocomplete) {
          autocomplete.focus();
        }
      });
    },

    removeById(id) {
      if (!this.enabled) return;
      this.innerValue = this.selectedValues.filter(
        (v) => this.getIdFromValue(v) !== id
      );
    },

    removeValue(v) {
      if (!this.enabled) return;

      const id = this.getIdFromValue(v);
      if (id == null) return;
      this.removeById(id);
    },

    clearGrouped() {
      if (!this.enabled) return;

      const first = this.selectedValues[0];
      this.innerValue = first != null ? [first] : [];
    },

    async onFocus() {
      if (!this.enabled) return;
      this.updateMenuWidth();
      if (!this.loadOnFocus) return;
      if (this.loading) return;

      if (this.search && this.search.length >= this.minSearchChars) {
        await this.load(false);
      } else if (!this.search) {
        await this.loadInitialData(true);
      }
    },

    handleFocus() {
      this.hasFocus = true;
      this.onFocus();
      this.$nextTick(() => {
        this.openMenu();
      });
    },

    handleBlur() {
      this.hasFocus = false;
      this.debouncedSearch.cancel();
      this.closeMenu();
    },

    handleClick() {
      if (!this.enabled) return;
      this.$nextTick(() => {
        this.focus();
        this.openMenu();
      });
    },

    async loadInitialData(force = false) {
      if (!this.enabled) return;
      if (this.loading) return;

      if (
        !force &&
        this.initialDataLoaded &&
        this.items.length > 0 &&
        !this.search
      )
        return;

      this.currentPage = 0;
      this.hasMoreItems = true;

      this.loading = true;
      const currentRequest = ++this.requestSequence;

      try {
        const res = await this.fetch(0, this.pageSize, "");
        if (currentRequest !== this.requestSequence) return;
        if (!this.enabled) return;
        this.processResponse(res, false, 0, "");
        this.initialDataLoaded = true;
      } catch (e) {
        this.$emit("error", e);
      } finally {
        if (currentRequest === this.requestSequence) this.loading = false;
      }
    },

    async load(isLoadMore = false) {
      if (!this.enabled) return;
      if (this.loadingMore && isLoadMore) return;

      const currentRequest = ++this.requestSequence;

      if (isLoadMore) {
        if (!this.hasMoreItems) return;
        this.loadingMore = true;
      } else {
        this.loading = true;
        this.items = [];
        this.currentPage = 0;
        this.hasMoreItems = true;
      }

      try {
        const pageToLoad = isLoadMore ? this.currentPage + 1 : 0;
        const res = await this.fetch(
          pageToLoad,
          this.pageSize,
          this.currentSearch
        );
        if (currentRequest !== this.requestSequence) return;
        if (!this.enabled) return;
        this.processResponse(res, isLoadMore, pageToLoad, this.currentSearch);
      } catch (e) {
        this.$emit("error", e);
      } finally {
        if (currentRequest === this.requestSequence) {
          if (isLoadMore) this.loadingMore = false;
          else this.loading = false;
        }
      }
    },

    processResponse(
      response,
      isLoadMore = false,
      pageToLoad = 0,
      searchTerm = ""
    ) {
      if (!this.enabled) return;

      const content = response.content || [];
      const last = !!response.last;

      let processed = [];

      if (
        !isLoadMore &&
        pageToLoad === 0 &&
        !searchTerm &&
        typeof this.prependItems === "function"
      ) {
        processed.push(...(this.prependItems() || []));
      }

      const mapped = this.mapItem ? content.map(this.mapItem) : content;
      processed.push(...mapped);

      if (isLoadMore) {
        this.items = this.mergeById(this.items, processed);
        this.currentPage = pageToLoad;
      } else {
        this.items = processed;
        this.currentPage = 0;
      }

      this.hasMoreItems = !last && content.length > 0;
      this.cacheSelected(this.innerValue);
    },

    mergeById(existing, incoming) {
      const map = new Map();
      existing.forEach((i) => map.set(this.getIdFromItem(i), i));
      incoming.forEach((i) => map.set(this.getIdFromItem(i), i));
      return Array.from(map.values());
    },

    handleSearch(searchTerm) {
      if (!this.enabled) return;
      this.debouncedSearch(searchTerm);
    },

    handleSearchDebounced(searchTerm) {
      if (!this.enabled) return;

      const next = searchTerm || "";
      const prev = this.currentSearch || "";

      if (next === "" && prev === "" && (this.loading || this.loadingMore))
        return;

      if (
        next === "" &&
        prev === "" &&
        this.initialDataLoaded &&
        this.items.length > 0
      )
        return;

      if (next === prev && next !== "") return;

      this.currentSearch = next;

      if (next === "") {
        this.requestSequence++;
        this.loading = false;
        this.loadingMore = false;

        this.items = [];
        this.currentPage = 0;
        this.hasMoreItems = true;

        this.loadInitialData(true);
        return;
      }

      if (next.length < this.minSearchChars) {
        this.requestSequence++;
        this.loading = false;
        this.loadingMore = false;

        this.items = [];
        this.currentPage = 0;
        this.hasMoreItems = true;
        return;
      }

      this.load(false);
    },

    clearSearch() {
      this.search = "";
      this.currentSearch = "";

      this.requestSequence++;

      if (this.loading || this.loadingMore) {
        this.loading = false;
        this.loadingMore = false;
      }

      if (!this.enabled) return;

      this.loadInitialData(true);
    },

    onIntersect(entries) {
      if (!this.enabled) return;
      if (!entries || !entries[0]) return;

      if (
        entries[0].isIntersecting &&
        this.infinite &&
        this.hasMoreItems &&
        !this.loading &&
        !this.loadingMore
      ) {
        this.load(true);
      }
    },

    refresh() {
      if (!this.enabled) return;

      if (
        this.currentSearch &&
        this.currentSearch.length >= this.minSearchChars
      )
        this.load(false);
      else this.loadInitialData(true);
    },

    refreshItems() {
      this.loadInitialData(true);
    },
  },
};
</script>

<style scoped>
.paginated-autocomplete-multi__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
}

.paginated-autocomplete-multi__content {
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.paginated-autocomplete-multi__title {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.paginated-autocomplete-multi__subtitle {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.8;
  font-size: 12px;
}

.paginated-autocomplete-multi__group-item {
  max-width: 240px;
  width: 100%;
  padding: 6px 12px;
  color: var(--v-foreground-base);
  font-size: 16px;
}

.paginated-autocomplete-multi__group-title {
  font-weight: medium;
  margin-bottom: 4px;
  color: var(--v-foreground-base);
  font-size: 18px;
}

.paginated-autocomplete-multi__group-row {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.paginated-autocomplete-multi__group-row + .paginated-autocomplete-multi__group-row {
  margin-top: 4px;
}

.paginated-autocomplete-multi__group-label {
  font-size: 14px;
}

.paginated-autocomplete-multi__chip {
  min-width: 0;
}

.paginated-autocomplete-multi__chip--main {
  flex: 0 1 auto;
  min-width: 0;
}

.paginated-autocomplete-multi__chip--group {
  flex: 0 0 auto;
}

.paginated-autocomplete-multi__chip-text {
  display: inline-block;
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.paginated-autocomplete-multi__group-card {
  max-width: 360px;
}

::v-deep(.paginated-autocomplete-multi .v-select__selections) {
  flex-wrap: nowrap !important;
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
}

::v-deep(.paginated-autocomplete-multi .v-select__selections > .v-chip) {
  margin-right: 6px;
}

::v-deep(.paginated-autocomplete-multi--searching input) {
  color: transparent !important;
  caret-color: var(--v-foreground-base);
}

::v-deep(.paginated-autocomplete-multi .v-list-item--active) {
  background-color: transparent !important;
}

::v-deep(.paginated-autocomplete-multi .v-list-item--active::before) {
  opacity: 0 !important;
}

::v-deep(.paginated-autocomplete-multi__checkbox) {
  margin-top: 0;
  padding-top: 0;
  transform: scale(0.8);
  transform-origin: left center;
}
</style>

<style lang="scss">
.paginated-autocomplete-multi__menu {
  box-sizing: border-box !important;
  overflow-x: hidden !important;

  .v-list-item {
    min-width: 0 !important;
  }

  .v-list-item__content {
    min-width: 0 !important;
    overflow: hidden;
  }

  .paginated-autocomplete-multi__item,
  .paginated-autocomplete-multi__content,
  .paginated-autocomplete-multi__title {
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
